import type { CitationReferenceData, TokenReference } from 'services/reference-generator/types';
import { citationReviewStatus, isAiCreatedCitation } from './reviewAggregator';

/**
 * SCAL-8970 - the manual-citation add decision, extracted pure.
 *
 * DEFAULT = APPEND (keep both citations). Replace is a deliberate, explicit
 * user choice that soft-dismisses the cell's AI citation on the backend; it
 * must never happen implicitly, so the drawer only offers it when
 * `shouldOfferReplace` says the choice is real.
 */

export type ManualCitationMode = 'append' | 'replace';

export interface ManualCitationTarget {
  fieldName: string | undefined;
  documentReferenceId: number;
  tokenReference: TokenReference;
}

const sameDocEntry = (refs: CitationReferenceData[], fieldName: string | undefined, documentReferenceId: number) =>
  refs.find(ref => ref.field_name === fieldName && ref.document_reference === documentReferenceId);

/**
 * Whether the add should prompt for Append vs Replace: only when the pushed
 * citation would CREATE a new entry (no same-document entry to merge into) and
 * the field already carries a visible (non-dismissed) AI-created citation.
 */
export const shouldOfferReplace = (
  existingRefs: CitationReferenceData[] | undefined,
  fieldName: string | undefined,
  documentReferenceId: number
): boolean => {
  const refs = existingRefs ?? [];
  return (
    !sameDocEntry(refs, fieldName, documentReferenceId) &&
    refs.some(
      ref =>
        ref.field_name === fieldName && isAiCreatedCitation(ref) && citationReviewStatus(ref) !== 'dismissed'
    )
  );
};

/**
 * Apply a pushed citation to a cell's citationReferences and return the next
 * array. Same document + field: merge the token into the existing entry (the
 * backend dedupes to that row regardless of mode). Otherwise append a new
 * entry stamped created_source 'human' (SCAL-9166); mode 'replace' adds the
 * request-side `replace` flag telling the save to soft-dismiss the field's
 * active AI citations. The client never edits the AI entry itself - dismissal
 * is the server's job, round-tripped on save.
 *
 * The `replace` flag stays on the entry until a refetch replaces the cell
 * state; that is safe (SCAL-8984): the backend only applies the dismissal
 * when the save actually CREATES the citation row, so a re-sent flag on an
 * already-persisted entry is a no-op.
 */
export const addManualCitation = (
  existingRefs: CitationReferenceData[] | undefined,
  { fieldName, documentReferenceId, tokenReference }: ManualCitationTarget,
  mode: ManualCitationMode
): CitationReferenceData[] => {
  const refs = existingRefs ?? [];
  const existing = sameDocEntry(refs, fieldName, documentReferenceId);
  if (existing) {
    existing.tokenReferences.push(tokenReference);
    existing.is_accepted = true;
    return refs;
  }
  return [
    ...refs,
    {
      document_reference: documentReferenceId,
      field_name: fieldName ?? '',
      is_accepted: true,
      created_source: 'human',
      tokenReferences: [tokenReference],
      ...(mode === 'replace' ? { replace: true } : {}),
    },
  ];
};
